import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useProject } from '../../contexts/ProjectContext';
import api from '../../lib/api';
import { Loader2, GanttChart, Calendar, CalendarDays, CalendarRange } from 'lucide-react';
import { cn } from '../../lib/utils';
import { addWeeks, isAfter, isBefore, startOfDay, startOfWeek, startOfMonth, startOfQuarter, addDays, addMonths, addQuarters, format, differenceInDays, differenceInWeeks, differenceInMonths, differenceInQuarters, eachDayOfInterval, eachWeekOfInterval, eachMonthOfInterval, eachQuarterOfInterval } from 'date-fns';
import { ROW_HEIGHT, WBS_ROW_HEIGHT_TOP, HEADER_HEIGHT } from './constants';
import { resolveColors, getBarColorForActivity } from '../../lib/colorTheme';
import { parseServerDate, selectActivityStart, selectActivityFinish } from '../../lib/date_format';

// Time scale configurations
const TIME_SCALES = {
  day: { label: 'Day', unitWidth: 30, minUnits: 60 },
  week: { label: 'Week', unitWidth: 60, minUnits: 24 },
  month: { label: 'Month', unitWidth: 80, minUnits: 18 },
  quarter: { label: 'Quarter', unitWidth: 120, minUnits: 8 }
};

// Darken a #RRGGBB hex color by `amount` (0..1) in HSL space.
// Used to render the proportional-fill overlay inside each Gantt bar so the
// "complete" portion reads darker than the "remaining" portion regardless of
// which status color the bar is using.
const darkenHex = (hex, amount = 0.25) => {
  if (typeof hex !== 'string' || !/^#[0-9A-Fa-f]{6}$/.test(hex)) return hex;
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l0 = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l0 > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h /= 6;
  }
  const l = Math.max(0, l0 * (1 - amount));
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  let nr; let ng; let nb;
  if (s === 0) { nr = ng = nb = l; } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    nr = hue2rgb(p, q, h + 1 / 3);
    ng = hue2rgb(p, q, h);
    nb = hue2rgb(p, q, h - 1 / 3);
  }
  const toHex = (x) => Math.round(x * 255).toString(16).padStart(2, '0');
  return `#${toHex(nr)}${toHex(ng)}${toHex(nb)}`;
};

const GanttTimeline = ({
  // Bumped by parent (SchedulingScreen.handleActivityChange) after a server
  // mutation lands. Children re-fetch in place via the dependency below;
  // they do NOT re-mount, so scroll position survives. Previously this was
  // wired as `key={`gantt-${refreshKey}`}` which destroyed the component
  // on every edit — see plan kind-questing-storm.md Layer 1.
  refreshKey = 0,
  // Separate signal from refreshKey: bumped by the parent's fast-path
  // merge handler (handleActivitiesMerged in SchedulingScreen) on every
  // bottom-panel save. The Gantt does not subscribe to the merged
  // allActivities state, so it needs its own re-fetch trigger here.
  mergedRevision = 0,
  selectedWbsId,
  selectedActivityId,
  onSelectActivity,
  onVerticalScroll,
  scrollRef,
  lookAheadWeeks = null,
  statusFilter = 'all',
  dateRangeFilter = { from: null, to: null },
  activitySearch = '',
  criticalPathOnly = false,
  scenarioData = null,
  expandedNodes: externalExpandedNodes = null,
  onExpandChange = null,
  // Baseline data for displaying baseline line overlay
  baselineColumnsData = null,
  // Controlled-prop override for layout template support
  timeScale: externalTimeScale = null,
  onTimeScaleChange = null,
}) => {
  const { activeSchedule, activeProject } = useProject();
  const colors = useMemo(
    () => resolveColors(activeProject?.color_scheme),
    [activeProject?.color_scheme]
  );
  const [wbsTree, setWbsTree] = useState([]);
  const [activitiesByWbs, setActivitiesByWbs] = useState({});
  const [internalExpandedNodes, setInternalExpandedNodes] = useState({});
  const [loading, setLoading] = useState(false);
  const scrollContainerRef = useRef(null);
  const [activities, setActivities] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const rowsContainerRef = useRef(null);

  // Tracks the schedule id of the last fetch so we know whether the current
  // run is the FIRST load for this schedule (auto-expand all WBS nodes) or a
  // refetch within the same schedule (preserve user's expansion state).
  const lastFetchedScheduleIdRef = useRef(null);

  // Time scale state — controllable via props (for layout templates)
  const [internalTimeScale, setInternalTimeScale] = useState('month');
  const timeScale = externalTimeScale ?? internalTimeScale;
  const setTimeScale = onTimeScaleChange ?? setInternalTimeScale;

  // Use external expanded nodes if provided, otherwise use internal state
  const expandedNodes = externalExpandedNodes !== null ? externalExpandedNodes : internalExpandedNodes;
  const setExpandedNodes = onExpandChange || setInternalExpandedNodes;

  // Expose scroll method via ref
  useEffect(() => {
    if (scrollRef) {
      scrollRef.current = {
        scrollTo: (scrollTop) => {
          if (rowsContainerRef.current) {
            rowsContainerRef.current.scrollTop = scrollTop;
          }
        }
      };
    }
  }, [scrollRef]);

  // Auto-center Gantt horizontally when activity is selected
  useEffect(() => {
    if (!selectedActivityId || !scrollContainerRef.current) return;
    
    // Find the selected activity
    let selectedAct = null;
    for (const acts of Object.values(activitiesByWbs)) {
      selectedAct = acts.find(a => a.id === selectedActivityId);
      if (selectedAct) break;
    }
    
    if (!selectedAct) return;
    
    // Get activity dates — canonical priority (actual → early → planned).
    const activityStart = selectActivityStart(selectedAct);
    const activityFinish = selectActivityFinish(selectedAct);
    
    if (!activityStart || !activityFinish) return;
    
    // Calculate position — parse server date strings as local dates so
    // the centering math agrees with the displayed timeline (see
    // lib/date_format.js for why bare `new Date(isoString)` is unsafe).
    const startDate = parseServerDate(activityStart);
    const finishDate = parseServerDate(activityFinish);
    if (!startDate || !finishDate) return;
    const midDate = new Date((startDate.getTime() + finishDate.getTime()) / 2);

    // Get timeline bounds — canonical priority via the shared helper.
    const allActs = Object.values(activitiesByWbs).flat();
    const timelineStart = allActs.reduce((min, act) => {
      const parsed = parseServerDate(selectActivityStart(act));
      if (!parsed) return min;
      return !min || parsed < min ? parsed : min;
    }, null);

    const timelineEnd = allActs.reduce((max, act) => {
      const parsed = parseServerDate(selectActivityFinish(act));
      if (!parsed) return max;
      return !max || parsed > max ? parsed : max;
    }, null);

    if (!timelineStart || !timelineEnd) return;
    
    // Calculate horizontal scroll position
    const totalMs = timelineEnd.getTime() - timelineStart.getTime();
    if (totalMs <= 0) return;
    
    const containerWidth = scrollContainerRef.current.offsetWidth;
    const totalWidth = Math.max(containerWidth, TIME_SCALES[timeScale].unitWidth * TIME_SCALES[timeScale].minUnits);
    
    const midMs = midDate.getTime() - timelineStart.getTime();
    const midPosition = (midMs / totalMs) * totalWidth;
    
    // Center the activity bar in the viewport
    const targetScroll = Math.max(0, midPosition - containerWidth / 2);
    
    // Smooth scroll to the position
    scrollContainerRef.current.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });
  }, [selectedActivityId, activitiesByWbs, timeScale]);

  // Fetch WBS tree structure and activities (or use scenario data)
  const fetchData = useCallback(async () => {
    if (!activeSchedule?.id) return;

    // First load for THIS schedule? Then we auto-expand everything; on
    // subsequent refetches (refreshKey bumps, scenario toggles within the
    // same schedule) we preserve whatever the user has expanded/collapsed.
    const isInitialLoadForSchedule =
      lastFetchedScheduleIdRef.current !== activeSchedule.id;
    lastFetchedScheduleIdRef.current = activeSchedule.id;
      
    setLoading(true);
    try {
      // Fetch WBS tree
      const wbsResponse = await api.get(`/wbs/schedule/${activeSchedule.id}/tree`);
      setWbsTree(wbsResponse.data);

      // Fetch relationships for dependency arrows
      const relResponse = await api.get(`/cpm/relationships/schedule/${activeSchedule.id}`).catch(() => ({ data: [] }));
      setRelationships(relResponse.data || []);

      // Auto-expand all nodes — only on initial load for this schedule;
      // subsequent refetches preserve the user's expansion state so an
      // edit doesn't collapse what they had open.
      if (isInitialLoadForSchedule) {
        const allExpanded = {};
        const expandAll = (nodes) => {
          nodes.forEach(node => {
            allExpanded[node.id] = true;
            if (node.children) expandAll(node.children);
          });
        };
        expandAll(wbsResponse.data);
        setExpandedNodes(allExpanded);
      }

      // Use scenario activities if available, otherwise fetch from main schedule
      if (scenarioData?.scenario_activities) {
        const grouped = {};
        const activityList = [];

        scenarioData.scenario_activities.forEach(scenarioAct => {
          const wbsId = scenarioAct.wbs_id;
          if (!grouped[wbsId]) {
            grouped[wbsId] = [];
          }

          // Transform scenario activity to match regular activity format
          const activity = {
            id: scenarioAct.id,
            activity_id: scenarioAct.activity_id,
            name: scenarioAct.name,
            wbs_id: scenarioAct.wbs_id,
            original_duration: scenarioAct.original_duration,
            remaining_duration: scenarioAct.remaining_duration,
            planned_start: scenarioAct.planned_start,
            planned_finish: scenarioAct.planned_finish,
            early_start: scenarioAct.early_start || scenarioAct.forecast_start,
            early_finish: scenarioAct.early_finish || scenarioAct.forecast_finish,
            late_start: scenarioAct.late_start,
            late_finish: scenarioAct.late_finish,
            total_float: scenarioAct.total_float,
            free_float: scenarioAct.free_float,
            is_critical: scenarioAct.is_critical,
            actual_start: scenarioAct.actual_start,
            actual_finish: scenarioAct.actual_finish,
            percent_complete: scenarioAct.percent_complete || 0,
            status: scenarioAct.status || 'not_started',
            is_scenario_activity: true,
            is_modified: scenarioAct.is_modified
          };

          grouped[wbsId].push(activity);
          activityList.push(activity);
        });

        setActivitiesByWbs(grouped);
        setActivities(activityList);
      } else {
        // Normal mode - fetch from main schedule
        const activitiesResponse = await api.get(`/activities/schedule/${activeSchedule.id}`);
        const grouped = {};
        activitiesResponse.data.forEach(activity => {
          if (!grouped[activity.wbs_id]) {
            grouped[activity.wbs_id] = [];
          }
          grouped[activity.wbs_id].push(activity);
        });
        setActivitiesByWbs(grouped);
        setActivities(activitiesResponse.data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
    // refreshKey is included so parent-driven refetches (post-edit) re-run
    // this effect WITHOUT remounting the component — scroll/UI state survives.
    // mergedRevision is bumped by the parent's fast-path merge handler on
    // every bottom-panel save; it triggers a re-fetch here so the Gantt
    // doesn't go stale while the table updates via the merged store.
  }, [activeSchedule?.id, scenarioData, setExpandedNodes, refreshKey, mergedRevision]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Toggle expand/collapse
  const toggleExpand = (nodeId) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
  };

  // Calculate timeline range based on time scale
  const { startDate, endDate, timeUnits, unitWidth } = useMemo(() => {
    const scaleConfig = TIME_SCALES[timeScale];
    const currentUnitWidth = scaleConfig.unitWidth;

    if (activities.length === 0) {
      // Default range from today
      const today = new Date();
      let start, end, units;

      switch (timeScale) {
        case 'day':
          start = startOfDay(today);
          end = addDays(start, scaleConfig.minUnits);
          units = eachDayOfInterval({ start, end }).map(d => ({
            label: format(d, 'd'),
            subLabel: format(d, 'EEE'),
            date: d,
            isWeekend: d.getDay() === 0 || d.getDay() === 6
          }));
          break;
        case 'week':
          start = startOfWeek(today, { weekStartsOn: 1 });
          end = addWeeks(start, scaleConfig.minUnits);
          units = eachWeekOfInterval({ start, end }, { weekStartsOn: 1 }).map(d => ({
            label: `W${format(d, 'w')}`,
            subLabel: format(d, 'MMM d'),
            date: d
          }));
          break;
        case 'quarter':
          start = startOfQuarter(today);
          end = addQuarters(start, scaleConfig.minUnits);
          units = eachQuarterOfInterval({ start, end }).map(d => ({
            label: `Q${Math.ceil((d.getMonth() + 1) / 3)}`,
            subLabel: format(d, 'yyyy'),
            date: d
          }));
          break;
        case 'month':
        default:
          start = startOfMonth(today);
          end = addMonths(start, scaleConfig.minUnits);
          units = eachMonthOfInterval({ start, end }).map(d => ({
            label: format(d, 'MMM'),
            subLabel: d.getFullYear() !== new Date().getFullYear() ? format(d, "''yy") : '',
            date: d
          }));
          break;
      }

      return { startDate: start, endDate: end, timeUnits: units, unitWidth: currentUnitWidth };
    }

    // Find min/max dates from activities
    let minDate = null;
    let maxDate = null;

    activities.forEach(a => {
      const start = selectActivityStart(a);
      const finish = selectActivityFinish(a);

      if (start) {
        const d = parseServerDate(start);
        if (d && (!minDate || d < minDate)) minDate = d;
      }
      if (finish) {
        const d = parseServerDate(finish);
        if (d && (!maxDate || d > maxDate)) maxDate = d;
      }
    });

    // Default range if no dates
    if (!minDate) minDate = new Date();
    if (!maxDate) maxDate = addMonths(minDate, 6);

    // Expand to full units based on scale
    let start, end, units;

    switch (timeScale) {
      case 'day':
        start = startOfDay(minDate);
        end = startOfDay(addDays(maxDate, Math.max(7, scaleConfig.minUnits - differenceInDays(maxDate, minDate))));
        units = eachDayOfInterval({ start, end }).map(d => ({
          label: format(d, 'd'),
          subLabel: format(d, 'EEE'),
          date: d,
          isWeekend: d.getDay() === 0 || d.getDay() === 6
        }));
        break;
      case 'week':
        start = startOfWeek(minDate, { weekStartsOn: 1 });
        end = startOfWeek(addWeeks(maxDate, Math.max(2, scaleConfig.minUnits - differenceInWeeks(maxDate, minDate))), { weekStartsOn: 1 });
        units = eachWeekOfInterval({ start, end }, { weekStartsOn: 1 }).map(d => ({
          label: `W${format(d, 'w')}`,
          subLabel: format(d, 'MMM d'),
          date: d
        }));
        break;
      case 'quarter':
        start = startOfQuarter(minDate);
        end = startOfQuarter(addQuarters(maxDate, Math.max(1, scaleConfig.minUnits - differenceInQuarters(maxDate, minDate))));
        units = eachQuarterOfInterval({ start, end }).map(d => ({
          label: `Q${Math.ceil((d.getMonth() + 1) / 3)}`,
          subLabel: format(d, 'yyyy'),
          date: d
        }));
        break;
      case 'month':
      default:
        start = startOfMonth(minDate);
        end = startOfMonth(addMonths(maxDate, Math.max(1, scaleConfig.minUnits - differenceInMonths(maxDate, minDate))));
        units = eachMonthOfInterval({ start, end }).map(d => ({
          label: format(d, 'MMM'),
          subLabel: d.getFullYear() !== new Date().getFullYear() ? format(d, "''yy") : '',
          date: d
        }));
        break;
    }

    // Ensure minimum number of units for proper scrolling
    return { startDate: start, endDate: end, timeUnits: units, unitWidth: currentUnitWidth };
  }, [activities, timeScale]);

  // Calculate bar position and width — canonical priority + timezone-safe parse.
  const getBarStyle = (activity) => {
    const start = selectActivityStart(activity);
    const finish = selectActivityFinish(activity);

    if (!start || !finish) return null;

    const startD = parseServerDate(start);
    const finishD = parseServerDate(finish);
    if (!startD || !finishD) return null;
    const startMs = startD.getTime();
    const finishMs = finishD.getTime();
    const totalMs = endDate.getTime() - startDate.getTime();
    if (totalMs <= 0) return null;
    const totalWidth = timeUnits.length * unitWidth;

    const left = ((startMs - startDate.getTime()) / totalMs) * totalWidth;
    const width = Math.max(((finishMs - startMs) / totalMs) * totalWidth, 20);

    return { left: `${left}px`, width: `${width}px` };
  };

  // Numeric version of getBarStyle for arrow coordinate calculations
  const getBarGeometry = useCallback((activity) => {
    const start = selectActivityStart(activity);
    const finish = selectActivityFinish(activity);
    if (!start || !finish) return null;

    const startD = parseServerDate(start);
    const finishD = parseServerDate(finish);
    if (!startD || !finishD) return null;
    const startMs = startD.getTime();
    const finishMs = finishD.getTime();
    const totalMs = endDate.getTime() - startDate.getTime();
    if (totalMs <= 0) return null;
    const totalWidth = timeUnits.length * unitWidth;

    const left = ((startMs - startDate.getTime()) / totalMs) * totalWidth;
    const width = Math.max(((finishMs - startMs) / totalMs) * totalWidth, 20);
    return { left, right: left + width };
  }, [startDate, endDate, timeUnits, unitWidth]);

  // Bar color resolution is delegated to the project's color scheme via
  // `getBarColorForActivity`. See `frontend/src/lib/colorTheme.js`.

  // Get baseline data for an activity (when baseline is selected)
  const getBaselineDataForActivity = useCallback((activityId) => {
    if (!baselineColumnsData?.activities) return null;
    // Match by activity.id (UUID)
    return baselineColumnsData.activities.find(a => a.activity_id === activityId || a.id === activityId);
  }, [baselineColumnsData]);

  // Calculate baseline line position and width (uses same date-to-pixel logic as main bar)
  const getBaselineLineStyle = useCallback((activity) => {
    const baselineData = getBaselineDataForActivity(activity.id);
    if (!baselineData) return null;

    // Get baseline start and finish dates
    const blStart = baselineData.baseline_start || baselineData.planned_start;
    const blFinish = baselineData.baseline_finish || baselineData.planned_finish;

    if (!blStart || !blFinish) return null;

    const startMs = new Date(blStart).getTime();
    const finishMs = new Date(blFinish).getTime();
    const totalMs = endDate.getTime() - startDate.getTime();
    if (totalMs <= 0) return null;
    const totalWidth = timeUnits.length * unitWidth;

    const left = ((startMs - startDate.getTime()) / totalMs) * totalWidth;
    const width = Math.max(((finishMs - startMs) / totalMs) * totalWidth, 10);

    return { left: `${left}px`, width: `${width}px` };
  }, [getBaselineDataForActivity, startDate, endDate, timeUnits, unitWidth]);

  // Apply filters to activities (VISIBILITY ONLY - matches UnifiedScheduleView filtering)
  const filteredActivitiesByWbs = useMemo(() => {
    if (!lookAheadWeeks && statusFilter === 'all' && !dateRangeFilter.from && !dateRangeFilter.to && !activitySearch.trim() && !criticalPathOnly) {
      return activitiesByWbs;
    }

    const today = new Date();
    const lookAheadEndDate = lookAheadWeeks ? addWeeks(today, lookAheadWeeks) : null;

    const filtered = {};
    Object.entries(activitiesByWbs).forEach(([wbsId, activities]) => {
      filtered[wbsId] = activities.filter(activity => {
        // Status filter
        if (statusFilter !== 'all' && activity.status !== statusFilter) {
          return false;
        }

        // Look-ahead filter — canonical priority so in-progress activities
        // aren't hidden when their early_start is outside the window.
        if (lookAheadWeeks) {
          const activityStart = selectActivityStart(activity);
          if (activityStart) {
            const startDate = parseServerDate(activityStart);
            // Show activities that start within the look-ahead window
            if (startDate && isAfter(startDate, lookAheadEndDate)) {
              return false;
            }
          }
        }

        // Date range filter
        if (dateRangeFilter.from || dateRangeFilter.to) {
          const activityStart = selectActivityStart(activity);
          const activityFinish = selectActivityFinish(activity);

          if (activityStart && activityFinish) {
            const startDate = parseServerDate(activityStart);
            const finishDate = parseServerDate(activityFinish);
            if (!startDate || !finishDate) return true;

            // Check if activity falls within or overlaps the date range
            if (dateRangeFilter.from && dateRangeFilter.to) {
              // Activity must overlap with date range
              if (isAfter(startDate, dateRangeFilter.to) || isBefore(finishDate, dateRangeFilter.from)) {
                return false;
              }
            } else if (dateRangeFilter.from) {
              // Activity must finish on or after the from date
              if (isBefore(finishDate, dateRangeFilter.from)) {
                return false;
              }
            } else if (dateRangeFilter.to) {
              // Activity must start on or before the to date
              if (isAfter(startDate, dateRangeFilter.to)) {
                return false;
              }
            }
          }
        }

        // Activity ID / Name text search
        if (activitySearch.trim()) {
          const q = activitySearch.toLowerCase();
          if (
            !activity.activity_id?.toLowerCase().includes(q) &&
            !activity.name?.toLowerCase().includes(q)
          ) return false;
        }

        // Critical path only
        if (criticalPathOnly && !activity.is_critical) return false;

        return true;
      });
    });

    return filtered;
  }, [activitiesByWbs, lookAheadWeeks, statusFilter, dateRangeFilter, activitySearch, criticalPathOnly]);

  // Count filtered activities for display
  const filteredActivityCount = useMemo(() => {
    return Object.values(filteredActivitiesByWbs).reduce((sum, arr) => sum + arr.length, 0);
  }, [filteredActivitiesByWbs]);

  // Flatten tree to get ordered list matching UnifiedScheduleView rendering
  const flattenedRows = useMemo(() => {
    const rows = [];

    const processNode = (node, level = 0) => {
      const isExpanded = expandedNodes[node.id];
      const nodeActivities = filteredActivitiesByWbs[node.id] || [];
      const hasChildren = node.children && node.children.length > 0;
      const hasActivities = nodeActivities.length > 0;
      const isTopLevel = level === 0;

      // Add WBS row
      rows.push({
        type: 'wbs',
        id: node.id,
        data: node,
        level,
        isTopLevel,
        height: isTopLevel ? WBS_ROW_HEIGHT_TOP : ROW_HEIGHT,
        hasChildren: hasChildren || hasActivities,
        isExpanded
      });

      // Add children and activities if expanded
      if (isExpanded) {
        // Child WBS nodes
        if (hasChildren) {
          node.children.forEach(child => processNode(child, level + 1));
        }
        // Activities under this WBS
        if (hasActivities) {
          nodeActivities.forEach(activity => {
            rows.push({
              type: 'activity',
              id: activity.id,
              data: activity,
              level: level + 1,
              height: ROW_HEIGHT,
              wbsId: node.id
            });
          });
        }
      }
    };

    wbsTree.forEach(node => processNode(node, 0));
    return rows;
  }, [wbsTree, filteredActivitiesByWbs, expandedNodes]);

  // Build coordinate map for arrow endpoints
  const { activityCoordMap, totalContentHeight } = useMemo(() => {
    const map = new Map();
    let yOffset = 0;
    for (const row of flattenedRows) {
      if (row.type === 'activity') {
        const geom = getBarGeometry(row.data);
        if (geom) {
          map.set(row.id, {
            yCenter: yOffset + row.height / 2,
            barLeft: geom.left,
            barRight: geom.right,
            isCritical: row.data.is_critical || row.data.total_float === 0,
          });
        }
      }
      yOffset += row.height;
    }
    return { activityCoordMap: map, totalContentHeight: yOffset };
  }, [flattenedRows, getBarGeometry]);

  // Compute dependency arrow SVG paths
  const arrowPaths = useMemo(() => {
    if (relationships.length === 0 || activityCoordMap.size === 0) return [];
    const STUB = 10;
    const arrows = [];

    for (const rel of relationships) {
      const pred = activityCoordMap.get(rel.predecessor_id);
      const succ = activityCoordMap.get(rel.successor_id);
      if (!pred || !succ) continue;

      const y1 = pred.yCenter;
      const y2 = succ.yCenter;
      let x1, x2;
      const type = rel.relationship_type || 'FS';

      switch (type) {
        case 'SS': x1 = pred.barLeft;  x2 = succ.barLeft;  break;
        case 'FF': x1 = pred.barRight; x2 = succ.barRight; break;
        case 'SF': x1 = pred.barLeft;  x2 = succ.barRight; break;
        case 'FS': default: x1 = pred.barRight; x2 = succ.barLeft; break;
      }

      // Orthogonal path routing
      const exitRight = type === 'FS' || type === 'FF';
      const enterLeft = type === 'FS' || type === 'SS';
      const exitX = exitRight ? x1 + STUB : x1 - STUB;
      const enterX = enterLeft ? x2 - STUB : x2 + STUB;
      let pathD;

      if (y1 === y2) {
        // Same row — detour above or below
        const detourY = exitX <= enterX ? y1 - 20 : y1 + 20;
        pathD = `M ${x1},${y1} H ${exitX} V ${detourY} H ${enterX} V ${y2} H ${x2}`;
      } else if ((exitRight && exitX <= enterX) || (!exitRight && exitX >= enterX)) {
        // Simple case — no crossing
        pathD = `M ${x1},${y1} H ${exitX} V ${y2} H ${x2}`;
      } else {
        // Complex — route via midpoint
        const midY = (y1 + y2) / 2;
        pathD = `M ${x1},${y1} H ${exitX} V ${midY} H ${enterX} V ${y2} H ${x2}`;
      }

      const isCritical = pred.isCritical && succ.isCritical;
      const isHighlighted = selectedActivityId &&
        (rel.predecessor_id === selectedActivityId || rel.successor_id === selectedActivityId);

      arrows.push({
        id: rel.id,
        pathD,
        color: isHighlighted ? '#3b82f6' : isCritical ? colors.critical : '#94a3b8',
        strokeWidth: isHighlighted ? 2 : 1.5,
        markerId: isHighlighted ? 'arr-sel' : isCritical ? 'arr-crit' : 'arr-norm',
      });
    }

    // Highlighted arrows render last (on top)
    arrows.sort((a, b) => (a.markerId === 'arr-sel' ? 1 : 0) - (b.markerId === 'arr-sel' ? 1 : 0));
    return arrows;
  }, [relationships, activityCoordMap, selectedActivityId, colors]);

  return (
    <div className="flex flex-col h-full bg-white" data-testid="gantt-timeline">
      {/* Header with Time Scale Controls - Matches UnifiedScheduleView header structure */}
      <div className="px-4 py-3 border-b border-slate-300 bg-slate-50 flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
              Gantt Timeline
            </h3>
            <div className="h-4 w-px bg-slate-300" />
            <span className="text-[10px] text-slate-400 whitespace-nowrap">
              {filteredActivityCount !== activities.length ? (
                <span className="text-blue-500">{filteredActivityCount} of {activities.length} activities (filtered)</span>
              ) : (
                <span>{activities.length} activities</span>
              )}
            </span>
          </div>
        </div>

        {/* Invisible spacer to match search input height */}
        <div className="h-7 invisible" />

        {/* Expand/Collapse spacer + Time Scale Selector (aligned with bottom row) */}
        <div className="flex items-center justify-between mt-2 flex-nowrap overflow-hidden">
          <div className="flex items-center gap-2 invisible flex-shrink-0">
            <span className="text-[10px]">Expand All</span>
            <span>|</span>
            <span className="text-[10px]">Collapse All</span>
          </div>

          {/* Time Scale Selector - Moved to bottom row to match column header alignment */}
          <div className="flex items-center gap-0.5 bg-white rounded-lg border border-slate-300 p-0.5 flex-shrink-0 mb-2.5" data-testid="time-scale-selector">
            {Object.entries(TIME_SCALES).map(([scale, config]) => (
              <button
                key={scale}
                onClick={() => setTimeScale(scale)}
                className={cn(
                  "px-2.5 py-1 text-[10px] font-medium rounded-md transition-all whitespace-nowrap",
                  timeScale === scale
                    ? "bg-blue-600 text-white shadow-sm"
                    : "text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                )}
                data-testid={`scale-${scale}`}
              >
                {config.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Timeline Content */}
      {loading ? (
        <div className="flex items-center justify-center py-8 flex-1">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        </div>
      ) : flattenedRows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 flex-1">
          <GanttChart className="h-8 w-8 text-slate-300 mb-2" />
          <p className="text-xs text-slate-500">No schedule data to display</p>
        </div>
      ) : (
        <div
          className="flex-1 overflow-auto min-h-0"
          ref={(el) => {
            scrollContainerRef.current = el;
            if (rowsContainerRef) rowsContainerRef.current = el;
          }}
          onScroll={(e) => {
            if (onVerticalScroll) {
              onVerticalScroll(e.target.scrollTop);
            }
          }}
        >
          <div style={{ minWidth: `${timeUnits.length * unitWidth}px` }}>
            {/* Time Unit Headers */}
            <div
              className="bg-slate-50 border-b border-slate-300 flex sticky top-0 z-10"
              style={{ height: `${HEADER_HEIGHT}px` }}
            >
              {/* Time unit columns */}
              {timeUnits.map((unit, i) => (
                <div
                  key={i}
                  className={cn(
                    "flex-shrink-0 px-1 flex flex-col items-center justify-center border-r border-slate-300",
                    unit.isWeekend && "bg-slate-100/50"
                  )}
                  style={{ width: `${unitWidth}px` }}
                >
                  <span className="text-[10px] font-medium text-slate-600 whitespace-nowrap">
                    {unit.label}
                  </span>
                  {unit.subLabel && (
                    <span className="text-[9px] text-slate-400 whitespace-nowrap">
                      {unit.subLabel}
                    </span>
                  )}
                </div>
              ))}
            </div>

            {/* Rows - WBS and Activities */}
            <div className="relative">
              {flattenedRows.map((row) => {
                if (row.type === 'wbs') {
                  return renderWbsRow(row);
                } else {
                  return renderActivityRow(row);
                }
              })}

              {/* Dependency arrow SVG overlay */}
              {arrowPaths.length > 0 && (
                <svg
                  className="absolute top-0 left-0 pointer-events-none"
                  style={{
                    width: `${timeUnits.length * unitWidth}px`,
                    height: `${totalContentHeight}px`,
                  }}
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <defs>
                    <marker id="arr-crit" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
                      <polygon points="0 0, 8 3, 0 6" fill={colors.critical} />
                    </marker>
                    <marker id="arr-norm" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
                      <polygon points="0 0, 8 3, 0 6" fill="#94a3b8" />
                    </marker>
                    <marker id="arr-sel" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
                      <polygon points="0 0, 8 3, 0 6" fill="#3b82f6" />
                    </marker>
                  </defs>
                  {arrowPaths.map(arrow => (
                    <path
                      key={arrow.id}
                      d={arrow.pathD}
                      fill="none"
                      stroke={arrow.color}
                      strokeWidth={arrow.strokeWidth}
                      markerEnd={`url(#${arrow.markerId})`}
                    />
                  ))}
                </svg>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="px-4 py-2 border-t border-slate-300 bg-slate-50 flex items-center gap-4 flex-shrink-0 overflow-hidden whitespace-nowrap">
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <div className="h-3 w-6 rounded flex-shrink-0" style={{ backgroundColor: colors.critical }} />
          <span className="text-[10px] text-slate-500">Critical Path</span>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <div className="h-3 w-6 rounded flex-shrink-0" style={{ backgroundColor: colors.in_progress }} />
          <span className="text-[10px] text-slate-500">In Progress</span>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <div className="h-3 w-6 rounded flex-shrink-0" style={{ backgroundColor: colors.completed }} />
          <span className="text-[10px] text-slate-500">Completed</span>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <div className="h-3 w-6 rounded flex-shrink-0" style={{ backgroundColor: colors.not_started }} />
          <span className="text-[10px] text-slate-500">Not Started</span>
        </div>
        {/* Baseline legend - only shown when baseline is selected */}
        {baselineColumnsData && (
          <div className="flex items-center gap-1.5 flex-shrink-0 pl-2 border-l border-slate-300">
            <div className="h-[2px] w-6 bg-slate-900 flex-shrink-0" />
            <span className="text-[10px] text-slate-500">Baseline</span>
          </div>
        )}
        {/* Dependency arrow legend */}
        {relationships.length > 0 && (
          <div className="flex items-center gap-1.5 flex-shrink-0 pl-2 border-l border-slate-300">
            <svg width="24" height="12" className="flex-shrink-0">
              <defs>
                <marker id="arr-legend" markerWidth="6" markerHeight="4" refX="6" refY="2" orient="auto">
                  <polygon points="0 0, 6 2, 0 4" fill="#94a3b8" />
                </marker>
              </defs>
              <line x1="0" y1="6" x2="18" y2="6" stroke="#94a3b8" strokeWidth="1.5" markerEnd="url(#arr-legend)" />
            </svg>
            <span className="text-[10px] text-slate-500">Dependency</span>
          </div>
        )}
      </div>
    </div>
  );

  // Render WBS row (no bar, just section indicator)
  function renderWbsRow(row) {
    const { data: wbs, level, isTopLevel, height, hasChildren, isExpanded, id } = row;

    return (
      <div
        key={`wbs-${id}`}
        className={cn(
          "flex items-center border-b border-slate-300 bg-slate-50/50",
          isTopLevel ? "bg-slate-100/70" : ""
        )}
        style={{ height: `${height}px` }}
      >
        {/* Timeline area for WBS (no bar, just grid lines) */}
        <div className="flex-1 relative" style={{ minWidth: `${timeUnits.length * unitWidth}px` }}>
          {/* Vertical grid lines */}
          <div className="absolute inset-0 flex pointer-events-none">
            {timeUnits.map((unit, i) => (
              <div
                key={i}
                className={cn(
                  "flex-shrink-0 border-r border-slate-100/50",
                  unit.isWeekend && "bg-slate-100/30"
                )}
                style={{ width: `${unitWidth}px` }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Render Activity row (with bar only, no label column)
  function renderActivityRow(row) {
    const { data: activity, level, height, id } = row;
    const isSelected = selectedActivityId === activity.id;
    const barStyle = getBarStyle(activity);
    const baselineLineStyle = baselineColumnsData ? getBaselineLineStyle(activity) : null;

    return (
      <div
        key={`activity-${id}`}
        className={cn(
          "flex items-center border-b border-slate-300 cursor-pointer transition-colors",
          isSelected ? "bg-blue-50" : "hover:bg-slate-50"
        )}
        style={{ height: `${height}px` }}
        onClick={() => onSelectActivity?.(isSelected ? null : activity)}
      >
        {/* Timeline with bar */}
        <div className="flex-1 relative" style={{ minWidth: `${timeUnits.length * unitWidth}px`, height: '100%' }}>
          {/* Vertical grid lines */}
          <div className="absolute inset-0 flex pointer-events-none">
            {timeUnits.map((unit, i) => (
              <div
                key={i}
                className={cn(
                  "flex-shrink-0 border-r border-slate-300",
                  unit.isWeekend && "bg-slate-50"
                )}
                style={{ width: `${unitWidth}px` }}
              />
            ))}
          </div>

          {/* Baseline line overlay - thin black line above the activity bar */}
          {baselineLineStyle && (
            <div
              className="absolute h-[2px] bg-slate-900 pointer-events-none"
              style={{
                ...baselineLineStyle,
                top: 'calc(50% - 12px)' // Position above the activity bar (bar is h-5=20px, centered, so move up ~12px)
              }}
              title={`Baseline: ${activity.activity_id}`}
            />
          )}

          {/* Activity bar */}
          {barStyle && (() => {
            const baseColor = getBarColorForActivity(activity, colors);
            const pct = Math.max(0, Math.min(100, activity.percent_complete || 0));
            return (
              <div
                className={cn(
                  "absolute top-1/2 -translate-y-1/2 h-5 rounded shadow-sm overflow-hidden",
                  isSelected ? "ring-2 ring-blue-500 ring-offset-1" : ""
                )}
                style={{ ...barStyle, backgroundColor: baseColor }}
                title={`${activity.activity_id}: ${activity.name} — ${pct}% complete`}
              >
                {pct > 0 && (
                  <div
                    className="absolute inset-y-0 left-0 pointer-events-none"
                    style={{ width: `${pct}%`, backgroundColor: darkenHex(baseColor, 0.25) }}
                    data-testid={`gantt-bar-fill-${activity.id}`}
                  />
                )}
                <span className="relative text-[9px] text-white font-medium px-1.5 truncate block leading-5">
                  {activity.name}
                </span>
              </div>
            );
          })()}
        </div>
      </div>
    );
  }
};

export default GanttTimeline;
